import { HttpRequest, HttpMethod } from "./http";

// POS หน้าร้าน — BO endpoints (#443, spec 2026-10-10-pos-module-design §4.3;
// phase 3 #445, spec 2026-10-10-pos-phase3-design §3.4 / §8.4).
// One method per B-endpoint under /api/v3/pos. The POS app's own device API
// (/api/pos/v1) is NOT called from the BO.
const BASE = "/api/v3/pos";

class PosProvider extends HttpRequest {
	// ---- devices (B1–B11)
	PosDeviceList(params) {
		return this.request(HttpMethod.Get, `${BASE}/devices`, params);
	}
	PosDeviceCreate(params) {
		return this.request(HttpMethod.Post, `${BASE}/devices`, params);
	}
	PosDeviceGet(id) {
		return this.request(HttpMethod.Get, `${BASE}/devices/${id}`);
	}
	PosDeviceUpdate(id, params) {
		return this.request(HttpMethod.Put, `${BASE}/devices/${id}`, params);
	}
	PosDeviceDelete(id) {
		return this.request(HttpMethod.Delete, `${BASE}/devices/${id}`);
	}
	PosDevicePairingCode(id) {
		return this.request(HttpMethod.Post, `${BASE}/devices/${id}/pairing-code`, {});
	}
	PosDeviceUnpair(id) {
		return this.request(HttpMethod.Post, `${BASE}/devices/${id}/unpair`, {});
	}
	PosDeviceProductsGet(id) {
		return this.request(HttpMethod.Get, `${BASE}/devices/${id}/products`);
	}
	PosDeviceProductsPut(id, params) {
		return this.request(HttpMethod.Put, `${BASE}/devices/${id}/products`, params);
	}
	// phase 3: B45 (supervisor) + the §11 staff allow-list
	devicePasscodeReset(id, params) {
		return this.request(HttpMethod.Post, `${BASE}/devices/${id}/passcode`, params);
	}
	PosDeviceStaffGet(id) {
		return this.request(HttpMethod.Get, `${BASE}/devices/${id}/staff`);
	}
	PosDeviceStaffPut(id, params) {
		return this.request(HttpMethod.Put, `${BASE}/devices/${id}/staff`, params);
	}

	// ---- events (B12–B20)
	PosEventList(params) {
		return this.request(HttpMethod.Get, `${BASE}/events`, params);
	}
	PosEventCreate(params) {
		return this.request(HttpMethod.Post, `${BASE}/events`, params);
	}
	PosEventGet(id) {
		return this.request(HttpMethod.Get, `${BASE}/events/${id}`);
	}
	PosEventUpdate(id, params) {
		return this.request(HttpMethod.Put, `${BASE}/events/${id}`, params);
	}
	PosEventDelete(id) {
		return this.request(HttpMethod.Delete, `${BASE}/events/${id}`);
	}
	PosEventDevicesPut(id, params) {
		return this.request(HttpMethod.Put, `${BASE}/events/${id}/devices`, params);
	}
	PosEventProductsPut(id, params) {
		return this.request(HttpMethod.Put, `${BASE}/events/${id}/products`, params);
	}
	PosEventReceiptGet(id) {
		return this.request(HttpMethod.Get, `${BASE}/events/${id}/receipt`);
	}
	PosEventReceiptPut(id, params) {
		return this.request(HttpMethod.Put, `${BASE}/events/${id}/receipt`, params);
	}
	PosEventReceiptDelete(id) {
		return this.request(HttpMethod.Delete, `${BASE}/events/${id}/receipt`);
	}

	// ---- POS products (B40–B44, phase 3; replace bundles + the bo_product picker)
	posProductList(params) {
		return this.request(HttpMethod.Get, `${BASE}/pos-products`, params);
	}
	posProductCreate(params) {
		return this.request(HttpMethod.Post, `${BASE}/pos-products`, params);
	}
	posProductGet(id) {
		return this.request(HttpMethod.Get, `${BASE}/pos-products/${id}`);
	}
	posProductUpdate(id, params) {
		return this.request(HttpMethod.Put, `${BASE}/pos-products/${id}`, params);
	}
	posProductDelete(id) {
		return this.request(HttpMethod.Delete, `${BASE}/pos-products/${id}`);
	}

	// ---- receiving account (B48 global, B49 per event)
	paymentAccountGet() {
		return this.request(HttpMethod.Get, `${BASE}/payment-account`);
	}
	paymentAccountPut(params) {
		return this.request(HttpMethod.Put, `${BASE}/payment-account`, params);
	}
	eventPaymentAccountGet(id) {
		return this.request(HttpMethod.Get, `${BASE}/events/${id}/payment-account`);
	}
	eventPaymentAccountPut(id, params) {
		return this.request(HttpMethod.Put, `${BASE}/events/${id}/payment-account`, params);
	}
	// §13.2 event staff
	eventStaffGet(id) {
		return this.request(HttpMethod.Get, `${BASE}/events/${id}/staff`);
	}
	eventStaffPut(id, params) {
		return this.request(HttpMethod.Put, `${BASE}/events/${id}/staff`, params);
	}
	eventPaymentAccountDelete(id) {
		return this.request(HttpMethod.Delete, `${BASE}/events/${id}/payment-account`);
	}

	// ---- settings (B24–B26)
	PosSettingGet() {
		return this.request(HttpMethod.Get, `${BASE}/settings`);
	}
	PosSettingPut(params) {
		return this.request(HttpMethod.Put, `${BASE}/settings`, params);
	}
	PosReceiptSettingGet() {
		return this.request(HttpMethod.Get, `${BASE}/receipt-settings`);
	}
	PosReceiptSettingPut(params) {
		return this.request(HttpMethod.Put, `${BASE}/receipt-settings`, params);
	}

	// ---- supervisor (B27–B36)
	PosLookups() {
		return this.request(HttpMethod.Get, `${BASE}/lookups`);
	}
	PosBillList(params) {
		return this.request(HttpMethod.Get, `${BASE}/bills`, params);
	}
	PosBillGet(id) {
		return this.request(HttpMethod.Get, `${BASE}/bills/${id}`);
	}
	PosBillReprint(id) {
		return this.request(HttpMethod.Post, `${BASE}/bills/${id}/reprint`, {});
	}
	PosBillVoid(id, params) {
		return this.request(HttpMethod.Post, `${BASE}/bills/${id}/void`, params);
	}
	PosReportSalesSummary(params) {
		return this.request(HttpMethod.Get, `${BASE}/reports/sales-summary`, params);
	}
	PosReportTopSpenders(params) {
		return this.request(HttpMethod.Get, `${BASE}/reports/top-spenders`, params);
	}
	PosReportCustomer(phone, params) {
		return this.request(HttpMethod.Get, `${BASE}/reports/customers/${encodeURIComponent(phone)}`, params);
	}
	PosReportProducts(params) {
		return this.request(HttpMethod.Get, `${BASE}/reports/products`, params);
	}
	PosReportPromotions(params) {
		return this.request(HttpMethod.Get, `${BASE}/reports/promotions`, params);
	}
	// B46 / B47 (phase 3): top spender per event by member id (mid)
	reportEventMembers(params) {
		return this.request(HttpMethod.Get, `${BASE}/reports/event-members`, params);
	}
	reportEventMember(mid, params) {
		return this.request(HttpMethod.Get, `${BASE}/reports/event-members/${encodeURIComponent(mid)}`, params);
	}
}

export default PosProvider;
